<script setup>
// Agent assist: one click drafts a reply from the conda brain (same retrieval +
// Heart as the bot, in the agent's voice). The agent reviews, inserts, edits,
// sends — nothing is ever sent automatically.
import { ref, watch } from 'vue';
import { condaApi } from '../condaApi';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});
const emit = defineEmits(['insert']);

const busy = ref(false);
const open = ref(false);
const draft = ref('');
const refs = ref([]);
const playbook = ref([]);
const error = ref('');

// New conversation selected → stale draft is meaningless.
watch(() => props.conversationId, () => { open.value = false; draft.value = ''; error.value = ''; });

async function suggest() {
  busy.value = true;
  error.value = '';
  draft.value = '';
  open.value = true;
  try {
    const r = await condaApi.assistSuggest(props.conversationId);
    draft.value = r.reply || '';
    refs.value = r.refs || [];
    playbook.value = r.playbook || [];
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
}

function insert() {
  if (draft.value) emit('insert', draft.value);
  open.value = false;
}
</script>

<template>
  <div class="conda-suggest border-b border-n-weak">
    <div class="flex items-center justify-between px-3 py-1">
      <button
        class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-text hover:underline disabled:opacity-50"
        :disabled="busy"
        @click="suggest"
      >
        ✨ {{ busy ? 'Drafting…' : open ? 'Suggest again' : 'Suggest reply' }}
      </button>
      <button
        v-if="open && !busy"
        class="text-xs text-n-slate-10 hover:text-n-slate-12"
        @click="open = false"
      >
        ✕
      </button>
    </div>
    <div v-if="open" class="px-3 pb-2">
      <div v-if="busy" class="text-xs text-n-slate-10 py-1">
        Reading the conversation and drafting…
      </div>
      <div v-else-if="error" class="text-xs text-n-ruby-11 py-1">⚠ {{ error }}</div>
      <template v-else-if="draft">
        <div
          class="text-sm text-n-slate-12 whitespace-pre-wrap rounded-lg border border-n-weak bg-n-slate-2 px-3 py-2 max-h-40 overflow-y-auto"
        >
          {{ draft }}
        </div>
        <div class="flex items-center gap-2 mt-1.5">
          <button
            class="text-xs font-medium px-2.5 py-1 rounded-md bg-n-brand text-white hover:brightness-110"
            @click="insert"
          >
            Insert into reply
          </button>
          <span v-if="refs.length || playbook.length" class="text-[11px] text-n-slate-10">
            based on: {{ refs.join(', ') }}<template v-if="playbook.length">{{ refs.length ? ' + ' : '' }}team playbook ({{ playbook.length }})</template>
          </span>
        </div>
      </template>
    </div>
  </div>
</template>
